import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Phase-7-E audit-event taxonomy. Mirrors `AuditEventType` from the SDK's
 * `types.ts` (kept inline so the portal doesn't take a hard dep on the
 * SDK package — it isn't in the npm workspace).
 */
export type AuditEventType =
  | 'tool.registered'
  | 'tool.updated'
  | 'tool.revoked'
  | 'tool.invoked'
  | 'ibct.minted'
  | 'ibct.redeemed'
  | 'ibct.revoked'
  | 'ibct.expired'
  | 'policy.upserted'
  | 'policy.deleted'
  | 'presentation.created'
  | 'zk.anchored'
  | 'delegation.created'
  | 'delegation.verified'
  | 'agent.state_changed'
  | 'legacy';

export interface AuditEvent {
  id: string;
  agentDid: string;
  eventType: AuditEventType | string;
  actorDid?: string | null;
  targetId?: string | null;
  payload: Record<string, unknown>;
  occurredAt: string;
}

interface IconStyle {
  icon: string;
  bg: string;
  fg: string;
}

const eventIconStyle: Record<string, IconStyle> = {
  'tool.registered': { icon: 'extension', bg: 'bg-blue-50', fg: 'text-blue-700' },
  'tool.updated': { icon: 'edit', bg: 'bg-blue-50', fg: 'text-blue-700' },
  'tool.revoked': { icon: 'block', bg: 'bg-red-50', fg: 'text-red-700' },
  'tool.invoked': { icon: 'play_arrow', bg: 'bg-emerald-50', fg: 'text-emerald-700' },
  'ibct.minted': { icon: 'token', bg: 'bg-purple-50', fg: 'text-purple-700' },
  'ibct.redeemed': { icon: 'redeem', bg: 'bg-purple-50', fg: 'text-purple-700' },
  'ibct.revoked': { icon: 'cancel', bg: 'bg-red-50', fg: 'text-red-700' },
  'ibct.expired': { icon: 'schedule', bg: 'bg-slate-100', fg: 'text-slate-600' },
  'policy.upserted': { icon: 'policy', bg: 'bg-amber-50', fg: 'text-amber-700' },
  'policy.deleted': { icon: 'delete', bg: 'bg-red-50', fg: 'text-red-700' },
  'presentation.created': { icon: 'badge', bg: 'bg-blue-50', fg: 'text-blue-700' },
  'zk.anchored': { icon: 'shield_lock', bg: 'bg-emerald-50', fg: 'text-emerald-700' },
  'delegation.created': { icon: 'swap_horiz', bg: 'bg-indigo-50', fg: 'text-indigo-700' },
  'delegation.verified': { icon: 'verified', bg: 'bg-emerald-50', fg: 'text-emerald-700' },
  'agent.state_changed': { icon: 'sync', bg: 'bg-amber-50', fg: 'text-amber-700' },
  legacy: { icon: 'history', bg: 'bg-slate-100', fg: 'text-slate-600' },
};

function styleFor(eventType: string): IconStyle {
  return eventIconStyle[eventType] ?? { icon: 'circle', bg: 'bg-slate-100', fg: 'text-slate-600' };
}

interface AuditTimelineProps {
  events: AuditEvent[];
  /** Pixel height of each row. Used by the windowing math. */
  rowHeight?: number;
  /** Container height in pixels (the timeline scrolls inside this). */
  height?: number;
}

/**
 * Virtualised audit-timeline list.
 *
 * Implementation notes:
 * - Pure in-component windowing — no `react-window` dep per the brief.
 * - `events` is expected pre-sorted (newest first) by the parent.
 * - Click-to-expand the payload (per-row `expanded` state held in a Set
 *   keyed by event id).
 */
export default function AuditTimeline({
  events,
  rowHeight = 64,
  height = 600,
}: AuditTimelineProps) {
  const { t } = useTranslation('audit');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onScroll = () => setScrollTop(el.scrollTop);
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // Compute heights with expanded payloads taken into account.
  const layout = useMemo(() => {
    const tops: number[] = [];
    let acc = 0;
    for (const ev of events) {
      tops.push(acc);
      acc += expanded.has(ev.id) ? rowHeight + 200 : rowHeight;
    }
    return { tops, totalHeight: acc };
  }, [events, expanded, rowHeight]);

  // Find first/last index visible. Simple linear scan — events are typically
  // O(hundreds); for >10k we'd switch to binary search.
  const visibleStart = useMemo(() => {
    const idx = layout.tops.findIndex((top) => top + rowHeight >= scrollTop);
    return idx === -1 ? 0 : Math.max(0, idx - 4); // overscan 4 rows
  }, [layout.tops, scrollTop, rowHeight]);

  const visibleEnd = useMemo(() => {
    const limit = scrollTop + height;
    let idx = visibleStart;
    while (idx < layout.tops.length && layout.tops[idx] < limit) idx++;
    return Math.min(events.length, idx + 4); // overscan 4 rows
  }, [layout.tops, visibleStart, scrollTop, height, events.length]);

  const slice = events.slice(visibleStart, visibleEnd);
  const offsetTop = layout.tops[visibleStart] ?? 0;

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-text-faint">
        <span className="material-symbols-outlined" style={{ fontSize: 32 }}>history</span>
        <p className="mt-2 text-sm font-semibold">{t('empty.title')}</p>
        <p className="mt-1 text-xs">{t('empty.subtitle')}</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="relative overflow-y-auto rounded-xl border border-border-subtle bg-white"
      style={{ height }}
    >
      <div style={{ height: layout.totalHeight, position: 'relative' }}>
        <div style={{ position: 'absolute', top: offsetTop, left: 0, right: 0 }}>
          {slice.map((ev) => {
            const style = styleFor(ev.eventType as string);
            const isOpen = expanded.has(ev.id);
            return (
              <div key={ev.id} className="border-b border-border-subtle">
                <button
                  onClick={() => toggle(ev.id)}
                  className="flex w-full items-start gap-3 px-5 py-3 text-left transition-colors hover:bg-slate-50/60"
                >
                  <div className={`mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg ${style.bg}`}>
                    <span className={`material-symbols-outlined ${style.fg}`} style={{ fontSize: 18 }}>
                      {style.icon}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm font-bold text-text-main">{t(`eventTypes.${ev.eventType}`, ev.eventType as string)}</span>
                      <span className="text-[10px] font-semibold text-text-faint">
                        {new Date(ev.occurredAt).toLocaleString()}
                      </span>
                    </div>
                    <div className="mt-0.5 flex flex-wrap gap-2 text-[11px] text-text-muted">
                      {ev.actorDid && (
                        <span><span className="font-semibold">{t('actor')}</span> <span className="font-mono">{ev.actorDid}</span></span>
                      )}
                      {ev.targetId && (
                        <span><span className="font-semibold">{t('target')}</span> <span className="font-mono">{ev.targetId}</span></span>
                      )}
                    </div>
                  </div>
                  <span className="material-symbols-outlined text-text-faint" style={{ fontSize: 18 }}>
                    {isOpen ? 'expand_less' : 'expand_more'}
                  </span>
                </button>
                {isOpen && (
                  <pre className="overflow-x-auto border-t border-border-subtle bg-slate-50/60 px-5 py-3 font-mono text-[11px] text-text-muted">
                    {JSON.stringify(ev.payload, null, 2)}
                  </pre>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
